import { JsonPipe, NgFor, NgIf } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { BreadcrumbComponent } from '../../../../../shared/components/breadcrumb/breadcrumb.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSortModule } from '@angular/material/sort';
import { QMSPaginatorComponent } from 'src/app/shared/components/qms-paginator/qms-paginator.component';
import { DateFormatPipe } from 'src/app/shared/pipes/date-format.pipe';
import { BreadcrumbItem } from 'src/app/shared/models/breadcrumb.interface';
import { DepartmentModel, SearchDepartmentFilter } from '../models/department.model';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { LogService } from 'src/app/shared/services/log.service';
import { UserService } from '../../../services/user.service';
import { PageEvent } from '@angular/material/paginator';
import { combineLatest, takeUntil } from 'rxjs';
import { DialogData } from 'src/app/shared/models/dialog-data.model';
import { DialogItemModel } from 'src/app/shared/models/dialog-item.model';
import { OptionModel } from 'src/app/shared/models/optionModel';
import commonFunction from 'src/app/shared/utils/common';
import { DepartmentService } from '../../../services/department.service';
import { SelectBasicDepartmentPopupComponent } from 'src/app/shared/components/select-basic-department-popup/select-basic-department-popup.component';
import { LogType } from 'src/app/shared/enum/log-type.enum';
import { LogModel } from 'src/app/shared/models/log.model';

@Component({
  selector: 'app-department-add-edit',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    DateFormatPipe,
    QMSPaginatorComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatTabsModule,
    MatSortModule,
    MatDatepickerModule,
    NgIf,
    NgFor,
    TranslateModule,
    AngularSvgIconModule,
    RouterModule,
    JsonPipe,
  ],
  templateUrl: './department-add-edit.component.html',
  styleUrl: './department-add-edit.component.scss',
})
export class DepartmentAddEditComponent {
  displayedColumns: string[] = ['fullName', 'username', 'workPhone', 'mobilePhone'];
  dataSource!: MatTableDataSource<DepartmentModel>;
  totalCount = 0;

  breadcrumbItems: BreadcrumbItem[] = [];
  filter: SearchDepartmentFilter = new SearchDepartmentFilter();
  pageSizeOptions: number[] = [10, 25, 100];
  departmentForm!: UntypedFormGroup;
  isAddMode = true;
  allDepartments: any[] = [];

  parentLocationId!: number;

  constructor(
    public formBuilder: UntypedFormBuilder,
    private translate: TranslateService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private departmentService: DepartmentService,
    private logService: LogService,
    private toastr: ToastrService,
  ) {
    this.departmentForm = formBuilder.group({
      name: ['', [Validators.required]],
      id: [''],
      departmentText: [''],
      description: [''],
      parentId: [0],
      currentUserIdLogin: [0],
      isCompany: [false],
      isResponsibles: [false],
      totalEmployees: [0],
      level: [0],
      childCount: [0],
      countryId: [0],
      address1: [''],
      address2: [''],
      address3: [''],
      email: [''],
      fax: [''],
      organizationNo: [''],
      phone: [''],
      url: [''],
      visitAddress1: [''],
      visitAddress2: [''],
      visitAddress3: [''],
      companyId: [''],
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.isAddMode = false;
        this.departmentForm.patchValue({ id: params['id'] });
        this.loadDataForEditMode();
      } else {
        this.isAddMode = true;
        this.loadDataForAddMode();
      }

      this.buildBreadcrumb();
    });
  }

  loadDataForAddMode() {
    combineLatest([this.departmentService.getAllDepartments(false)]).subscribe(([allDepartments]) => {
      this.allDepartments = allDepartments;
    });
  }

  loadDataForEditMode() {
    combineLatest([
      this.departmentService.getDepartmentById(this.departmentForm.value.id),
      this.departmentService.getAllDepartments(false),
    ]).subscribe(([departmentDetail, allDepartments]) => {
      this.departmentForm.patchValue(departmentDetail);
      this.allDepartments = allDepartments;
      this.departmentForm.patchValue({
        departmentText: commonFunction.buildLocationText(departmentDetail.id, this.allDepartments),
      });
      this.parentLocationId = departmentDetail.parentId;
    });
  }

  selectedTabChange(changeEvent: MatTabChangeEvent) {
    if (changeEvent.index === 1) {
      this.departmentService
        .getEmployeeOfDepartmentPaging(this.departmentForm.value.id, this.filter)
        .subscribe((res) => {
          this.dataSource = new MatTableDataSource<DepartmentModel>(res.departmentEmployees);
          this.totalCount = res.totalCount;
        });
    }
  }

  buildBreadcrumb() {
    this.breadcrumbItems = [
      {
        name: 'SIDE_BAR.HOME',
        url: '/',
      },
      {
        name: 'SIDE_BAR.MANAGE_DEPARTMENT',
        url: '/admin/departments',
      },
      {
        name: this.isAddMode ? 'DEPARTMENTS.ADD_DEPARTMENT' : 'DEPARTMENTS.EDIT_DEPARTMENT',
        url: '',
      },
    ];
  }

  loadData() {}

  onClickSelectDepartment(): void {
    const data: DialogData = new DialogData();
    data.selectNode = new OptionModel('', 0);
    data.items = this.allDepartments.map(
      (item) => new DialogItemModel(item.name, item.id, item.parentId, item.isReadOnly),
    );
    data.showRoot = true;
    data.canChooseRoot = true;
    data.isGuidId = false;
    data.title = this.translate.instant('LOCATION.SELECT_LOCATION');
    const dialogRef = this.dialog.open(SelectBasicDepartmentPopupComponent, {
      minWidth: `650px`,
      height: `395px`,
      data,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.id !== this.departmentForm.value.id) {
        console.log(result);
        this.departmentForm.patchValue({ parentId: result.id });

        this.departmentForm.patchValue({
          departmentText: result.id === 0 ? '' : commonFunction.buildLocationText(result.id, this.allDepartments),
        });
      }
    });
  }

  onSubmit(): void {
    if (!this.departmentForm.valid) {
      return;
    }

    if (this.departmentForm.value.id) {
      const params = {
        ...this.departmentForm.value,
      };

      this.departmentService.updateDepartmentInformation(params).subscribe((res) => {
        this.toastr.success(this.translate.instant('DEPARTMENTS.DEPARTMENT_UPDATED'));
        this.saveLogUpdateDepartment(this.departmentForm.value.name);
        this.saveLogChangeDepartment(this.departmentForm.value.parentId);
        this.router.navigate(['admin/departments']);
      });
    } else {
      const params = {
        department: this.departmentForm.value,
        positions: [],
        leaders: {
          employeeIds: [],
          addedEmployeeNames: [],
          removedEmployeeNames: [],
        },
        caseWorkers: {
          employeeIds: [],
          addedEmployeeNames: [],
          removedEmployeeNames: [],
        },
      };
      this.departmentService.createDepartment(params).subscribe((res) => {
        this.toastr.success(this.translate.instant('DEPARTMENTS.NEW_DEPARTMENT_CREATED'));

        this.saveLogCreateDepartment(this.departmentForm.value.name);
        this.departmentForm.value.id = res;
        const newDepartment = {
          id: res,
          name: this.departmentForm.value.name,
          parentId: this.departmentForm.value.parentId ? this.departmentForm.value.parentId : 0,
        };
        this.allDepartments.push(newDepartment);
        this.departmentForm.patchValue({
          departmentText: commonFunction.buildLocationText(res, this.allDepartments),
        });
      });
    }
  }

  saveLogChangeDepartment(parentId: number): void {
    if (this.parentLocationId != parentId) {
      const oldLocation = this.allDepartments.find((x) => x.id == this.parentLocationId).name;
      const newLocation = this.allDepartments.find((x) => x.id == parentId).name;
      const log: LogModel = new LogModel();
      log.logType = LogType.changeDepartment;
      log.description = ` change location from <strong>${oldLocation}</strong> to <strong>${newLocation}</strong> of <strong>${this.departmentForm.value.name}</strong>`;
      log.descriptionNo = ` endre plassering fra <strong>${oldLocation}</strong> til <strong>${newLocation}</strong> av <strong>${this.departmentForm.value.name}</strong>`;
      this.logService.saveLog(log).subscribe();
    }
  }

  saveLogCreateDepartment(name: string): void {
    const log: LogModel = new LogModel();
    log.logType = LogType.createDepartment;
    log.description = ` created department <strong>${name}</strong>`;
    log.descriptionNo = ` opprettet avdeling <strong>${name}</strong>`;
    this.logService.saveLog(log).subscribe();
  }

  saveLogUpdateDepartment(name: string): void {
    const log: LogModel = new LogModel();
    log.logType = LogType.updateDepartment;
    log.description = ` update department  <strong>${name}</strong>`;
    log.descriptionNo = ` oppdateringsavdeling <strong>${name}</strong>`;
    this.logService.saveLog(log).subscribe();
  }

  searchByKeyword(): void {
    this.filter.pageIndex = 0;
    this.filter.pageIndex = 0;
    this.selectedTabChange({ index: 1 } as MatTabChangeEvent);
  }

  clearFilter(): void {
    this.filter.keyWord = '';
  }

  pageEvent(event: PageEvent) {
    this.filter.pageIndex = event.pageIndex;
    this.filter.pageSize = event.pageSize;
    this.selectedTabChange({ index: 1 } as MatTabChangeEvent);
  }
}
